import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {getLocalPosition, getRotScaleFromCSSTranform} from "../../../utils";
import ZoomMapComponent from "../ZoomMapComponent";
import {useDispatch, useSelector} from "react-redux";
import {ApplicationState} from "../../../store";
import {LayoutState} from "../../../store/layouts/types";
import './style.scss';
import {IconComponent} from "../simpleComponents";
import { RouterState } from 'connected-react-router';
import { CamerasState } from '../../../store/cameras/types';

const {CONFIG} = window;

interface Props {
    children: ReactElement[] | ReactElement,
    enable:boolean,
    pane: number,
    digitalPTZ?: boolean,
    PTZCapibility?: any
}

interface Point {
    x: number,
    y: number
}

interface Rectangle extends Point {
    width: number,
    height: number
}

const getAnchor = (elem: HTMLElement, {x, y}: Point) => {
    const { left, top, width, height } = elem.getBoundingClientRect();
    return {
        x: (x - left) / width,
        y: (y - top) / height,
    }
};

const getScale = (elem: HTMLElement, w: number, h: number) => {
    const { width, height } = elem.getBoundingClientRect();
    let scale = (height / h + width / w) / 2;
    scale = adjustScale(scale);
    return scale
};

const adjustScale = (scale : number) => {
    let s = scale > limitScale ? limitScale : scale;
    s = s < 1 ? 1 : s;
    return s
};

const setElementPosition = (elem: HTMLElement, { x, y, width, height }: Rectangle) => {
    elem.style.left = x + 'px';
    elem.style.top = y + 'px';
    elem.style.width = width + 'px';
    elem.style.height = height + 'px';
};

const getMult = (anchor: Point) => {
    return {
        x: 0.5 - anchor.x,
        y: 0.5 - anchor.y
    }
};

const { scrollStep, moveStep, limitScale } = typeof (window.CONFIG) !== 'undefined' ? CONFIG.zoom : {scrollStep: '', moveStep: '', limitScale: ''};

const ZoomComponent: React.FC<Props> = ({enable, children, pane, digitalPTZ, PTZCapibility}) => {
    const initialTransform = {mult: {x: 0, y: 0}, scale: 1, anchor: {x: 0.5, y: 0.5}};
    const [transform, setTransform] = useState(initialTransform);
    const refZoom = useRef<HTMLDivElement | null>(null);
    const refRect = useRef<HTMLDivElement | null>(null);
    const digZoomRef = useRef<HTMLDivElement | null>(null);
    const [ptzBlocked, setPTZBlocked] = useState(false);
    const {expandedVideoLayout} = useSelector<ApplicationState, LayoutState>(state => state.layouts);
    const {location} = useSelector<ApplicationState, RouterState>(state => state.router);
    const {currentCameras} = useSelector<ApplicationState, CamerasState>(state => state.cameras);
    const dispatch = useDispatch();
    const path = location.pathname;
    let ptzZoomInterval = null;

    let startParams : Point | null = null;
    let shiftedAnchor : Point | null = null;

    let rectParams : {start: Point, end: Point} = {
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 }
    };

    const handleScroll = (e: any) => {
        if (!enable) return;
        const direction = e.deltaY > 0 ? 1 : -1;

        if (digitalPTZ && digZoomRef.current) {
            if (!ptzBlocked && PTZCapibility && PTZCapibility.has_ptz && PTZCapibility.has_ptz_relative) {
                direction === 1
                    ? dispatch({type: 'ptzZoomIn', pane})
                    : dispatch({type: 'ptzZoomOut', pane});

                setPTZBlocked(true);
                ptzZoomInterval = setTimeout(() => {
                    setPTZBlocked(false)
                }, 50)

            }
        } else {
            const scale = adjustScale(transform.scale + direction * scrollStep);
            setTransform({...transform, scale})
        }

    };

    const downHandler = (e: React.MouseEvent) => {
        if (!enable) return;
        e.stopPropagation();
        const { clientX, clientY } = e;
        startParams = { x: clientX, y: clientY };
        rectParams = {start: {x: clientX, y: clientY}, end: {x: clientX, y: clientY}};
        setRectanglePosition();
    };

    const moveHandler = (e: React.MouseEvent) => {
        if (!enable) return;
        e.stopPropagation();
        if (!startParams) return;

        if (digitalPTZ && digZoomRef.current) {
            resizeRectangle(e)
        } else {
            transform.scale === 1 ? resizeRectangle(e) : moveZoom(e)
        }

    };

    const upHandler = (e: React.MouseEvent) => {
        if (!enable) return;
        if (!startParams) return;
        startParams = null;

        if (digitalPTZ && digZoomRef.current) {
            //TODO should be implemented digital handler;
            doDigitalZoom();
        } else {
            shiftedAnchor ? applyShiftedAnchor() : doZoom();
        }

    };

    const doZoom = () => transform.scale === 1 ? zoomIn() : zoomOut();

    const applyShiftedAnchor = () => {
        if (!shiftedAnchor) return;
        const mult = getMult(shiftedAnchor);
        setTransform({...transform, mult, anchor: shiftedAnchor});
    };

    //******** mooving zoom ****************
    const resizeRectangle = (e: React.MouseEvent) => {
        const { start, end } = rectParams;
        const { clientX, clientY } = e;
        if (!startParams) return;

        if (clientX > startParams.x) {
            start.x = startParams.x;
            end.x = clientX;
        } else {
            end.x = startParams.x;
            start.x = clientX;
        }

        if (clientY > startParams.y) {
            start.y = startParams.y;
            end.y = clientY;
        } else {
            end.y = startParams.y;
            start.y = clientY;
        }
        setRectanglePosition()
    };


    const moveZoom = (e: React.MouseEvent) => {
        const { start, end } = rectParams;
        const { scale, anchor } = transform;
        const { clientX, clientY } = e;
        end.x = clientX;
        end.y = clientY;

        shiftedAnchor = {
            x: anchor.x - (end.x - start.x) * moveStep / scale,
            y: anchor.y - (end.y - start.y) * moveStep / scale
        };

        shiftedAnchor.x = shiftedAnchor.x > 1 ? 1 : shiftedAnchor.x;
        shiftedAnchor.x = shiftedAnchor.x < 0 ? 0 : shiftedAnchor.x;
        shiftedAnchor.y = shiftedAnchor.y > 1 ? 1 : shiftedAnchor.y;
        shiftedAnchor.y = shiftedAnchor.y < 0 ? 0 : shiftedAnchor.y;

        setPosition(scale, getMult(shiftedAnchor));
    };

    const setRectanglePosition = () => {
        const elem = refZoom.current;
        const rectElement = refRect.current;

        if (elem && rectElement) {
            const { start, end } = rectParams;
            const coords = { x: start.x, y: start.y };
            const { x, y } = getLocalPosition(elem, coords, transform.scale);
            const width = end.x - start.x;
            const height = end.y - start.y;
            setElementPosition(rectElement, {x, y, width, height})
        }
    };

    const setPosition = (scale: number, mult: Point) => {
        const elem = refZoom.current;
        const prevParams = elem ? getRotScaleFromCSSTranform(elem) : null;

        if (elem && prevParams) {
            const { width, height } = elem.getBoundingClientRect();
            const w = width / prevParams.scale * scale;
            const h = height / prevParams.scale * scale;

            elem.style.left = mult.x * (w - w / scale) + 'px';
            elem.style.top = mult.y * (h - h / scale) + 'px';
            elem.style.transform = `scale(${scale}, ${scale})`;
        }

    };

    const zoomIn = () => {
        const elem = refZoom.current;
        const { start, end } = rectParams;
        if (!elem) return;

        const x = (start.x + end.x) / 2;
        const y = (start.y + end.y) / 2;
        const w = end.x - start.x;
        const h = end.y - start.y;
        const anchor = getAnchor(elem, {x, y});
        const scale = getScale(elem, w, h);
        const mult = getMult(anchor);
        rectParams = {
            start: { x: 0, y: 0 },
            end: { x: 0, y: 0 }
        };
        setRectanglePosition();
        if (end.x - start.x < 5 || end.y - start.y < 5) {
            setTransform(initialTransform)
        } else {
            setTransform({scale, mult, anchor});
        }

    };

    const zoomOut = () => setTransform(initialTransform);

    const doDigitalZoom = () => {
        const el = digZoomRef.current;
        const rectEl = refRect.current;
        if (el && rectEl) {
            const elBounds = el.getBoundingClientRect();
            const rectElBounds = rectEl.getBoundingClientRect();
            const { start, end } = rectParams;

            const coords = ((end.x - start.x < 10) || (end.y - start.y) < 10)
                ? {
                    left: (start.x - elBounds.left) / elBounds.width,
                    top: (start.y - elBounds.top) / elBounds.height
                }
                : {
                    left: (start.x - elBounds.left) / elBounds.width,
                    top: (start.y - elBounds.top) / elBounds.height,
                    right: (end.x - elBounds.left) / elBounds.width,
                    bottom: (end.y - elBounds.top) / elBounds.height
                };

            dispatch({type: 'doPTZaction', pane, coords});
            setElementPosition(rectEl, {x: 0, y: 0, width: 0, height: 0})
        }
    };

    useEffect(() => {
        const {scale, mult} = transform;
        setPosition(scale, mult)
    }, [transform, expandedVideoLayout]);

    useEffect(() => {
        if (digitalPTZ) zoomOut();
    }, [digitalPTZ]);

    return (
        <div  onMouseDown={ downHandler }
              onMouseMove={ moveHandler }
              onClick={ upHandler }
              onMouseLeave ={ upHandler }
              ref={digZoomRef}
              className='w-100 h-100'
        >
            <div className='zoom pos-abs' ref={refZoom}
                onWheel={ handleScroll }
            >
                { children }
            </div>
            <div className='rect' ref={ refRect } />
            {
                transform.scale > 1 && <ZoomMapComponent scale={transform.scale}
                                                         anchor={transform.anchor}
                />
            }
            {
                enable === true ? (
                    <div className='zoom-buttons'>
                        <IconComponent handler={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleScroll({deltaY: 1})
                        }}
                                       classValue={'zoom-buttons__in icon'}
                                       clickedClassValue={' active'}
                                       title="Zoom In"
                        />
                        <IconComponent handler={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleScroll({deltaY: -1})
                        }}
                                       classValue={'zoom-buttons__out icon'}
                                       clickedClassValue={' active'}
                                       title="Zoom Out"
                        />
                    </div>
                ) : ''
            }
        </div>
    )
};


export default ZoomComponent